import { useSyncExternalStore } from 'react'
import { mutate } from 'swr'
import { createEndpointBreaker } from 'utils/endpointBreaker'

/**
 * Availability of the farms' staked liquidity. Since 2026-10-03 it is read on chain (fetchFarmsStakedTvl: the
 * MasterChefV3's position NFTs valued at each pool's slot0); farms.cadinu.io `/v3/56/liquidity/<lp>` answered
 * Cloudflare 1101 from 2026-09-28. When the read fails, APR and "Staked Liquidity" show as unavailable, never as 0.
 */
export const farmsTvlSource = createEndpointBreaker({ name: 'farms-liquidity-api', failuresToOpen: 2, cooldownMs: 60_000 })

export type FarmTvlStatus = 'loading' | 'ready' | 'unavailable'

interface TvlStatusState {
  byLp: Record<string, FarmTvlStatus>
  fetchedOnce: boolean
  priceReady: boolean
}

let state: TvlStatusState = { byLp: {}, fetchedOnce: false, priceReady: false }
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

export const setFarmTvlStatuses = (byLp: Record<string, FarmTvlStatus>, priceReady: boolean) => {
  state = { byLp, fetchedOnce: true, priceReady }
  emit()
}

const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => {
    listeners.delete(l)
  }
}
const getSnapshot = () => state

/** status of the TVL / APR figures of one farm */
export const useFarmTvlStatus = (lpAddress?: string): FarmTvlStatus => {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  if (!snapshot.fetchedOnce) return 'loading'
  if (!lpAddress) return 'unavailable'
  return snapshot.byLp[lpAddress.toLowerCase()] ?? 'unavailable'
}

/** manual retry: forget the breaker state and refetch the farm TVL query */
export const retryFarmTvl = () => {
  farmsTvlSource.reset()
  mutate((key) => Array.isArray(key) && key[1] === 'cake-apr-tvl')
}
